// AdminLayout — LIGHT mode minimal sidebar for the platform admin portal.
import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { PanorixLogo } from '@/components/PanorixLogo';
import NotificationCenter from '@/components/NotificationCenter';
import {
  LayoutDashboard, Users, FolderKanban, Layers, Shield, FileSearch, Mail, Plug,
  ToggleRight, KeyRound, Building2, Sparkles, LogOut, ArrowLeft, ShieldAlert, ShieldCheck,
  Cloud, BrainCircuit, Rocket,
} from 'lucide-react';

const NAV = [
  { to: '/admin',                icon: LayoutDashboard, label: 'Overview',         end: true },
  { to: '/admin/organizations',  icon: Building2,       label: 'Organizations' },
  { to: '/admin/trial-signups',  icon: Sparkles,        label: 'Trial Signups' },
  { to: '/admin/users',          icon: Users,           label: 'Users' },
  { to: '/admin/projects',       icon: FolderKanban,    label: 'Projects' },
  { to: '/admin/modules',        icon: Layers,          label: 'Module Registry' },
  { to: '/admin/role-templates', icon: Shield,          label: 'Role Templates' },
  { to: '/admin/audit',          icon: FileSearch,      label: 'Audit Log' },
  { to: '/admin/invitations',    icon: Mail,            label: 'Invitations' },
  { to: '/admin/integrations',   icon: Plug,            label: 'Integrations' },
  { to: '/admin/feature-flags',  icon: ToggleRight,     label: 'Feature Flags' },
  { to: '/admin/licences',       icon: ShieldCheck,     label: 'Customer Licences' },
  { to: '/admin/license',        icon: KeyRound,        label: 'License & Tenants' },
  { to: '/admin/cloud-deploy',   icon: Cloud,           label: 'Deploy on AWS' },
  { to: '/admin/move-to-cloud',  icon: Rocket,          label: 'Move This App' },
  { to: '/admin/ai-engine',      icon: BrainCircuit,    label: 'AI Engine' },
];

export default function AdminLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [_collapsed] = useState(false);

  const isPlatformAdmin = user?.platformRole === 'SUPERADMIN';

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <div className="max-w-md text-center rounded-xl p-10 border border-slate-200 bg-white shadow-sm" data-testid="admin-restricted">
          <ShieldAlert className="w-12 h-12 mx-auto mb-4 text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Superadmin Access Only</h2>
          <p className="text-sm text-slate-500 mb-6">The Admin Portal is restricted to the Capexara superadmin.</p>
          <button onClick={() => navigate('/project-selector')} className="text-xs uppercase tracking-[0.2em] bg-[#F26B1F] hover:bg-[#D85A0F] text-white px-5 py-2.5 rounded transition">
            ← Back to Control Tower
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = async () => { await logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-background text-foreground flex" style={{ fontFamily: '"DM Sans", "Inter", sans-serif' }}>
      {/* Sidebar — light */}
      <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col">
        <div className="px-5 py-5 border-b border-border flex items-center gap-3">
          <PanorixLogo width={48} height={48} showWordmark={false} />
          <div className="leading-none">
            <div className="text-[15px] font-medium tracking-[0.05em] text-foreground" style={{ fontFamily: "'Noto Sans','Inter',sans-serif" }}>
              Dṛ<span style={{color:'#F26B1F'}}>ṣ</span>ṭi
            </div>
            <div className="text-[9px] tracking-[0.25em] text-[#F26B1F] uppercase mt-1">Admin Portal</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.map((item, i) => (
            <motion.div key={item.to}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.02, duration: 0.2 }}>
              <NavLink to={item.to} end={!!item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] transition-all ${
                    isActive
                      ? 'bg-[#F26B1F]/10 text-[#B24D0F] font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`
                }>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span layoutId="admin-nav-indicator"
                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#F26B1F]"
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                    )}
                    <item.icon className="w-4 h-4 shrink-0 group-hover:scale-110 transition-transform" strokeWidth={1.8} />
                    <span className="truncate">{item.label}</span>
                  </>
                )}
              </NavLink>
            </motion.div>
          ))}
        </nav>

        <div className="border-t border-border p-3 space-y-1">
          <button onClick={() => navigate('/project-selector')} data-testid="admin-back-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-muted-foreground hover:text-foreground hover:bg-muted transition">
            <ArrowLeft className="w-4 h-4" /> Control Tower
          </button>
          <button onClick={handleLogout} data-testid="admin-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-[13px] text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition">
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-card/85 border-b border-border px-8 py-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            {subtitle && <div className="text-[10px] tracking-[0.3em] text-muted-foreground uppercase mb-1">{subtitle}</div>}
            <h1 className="text-xl font-medium text-foreground truncate">{title}</h1>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <NotificationCenter />
            {actions}
            <div className="text-right">
              <div className="text-[12px] text-foreground">{user?.fullName || user?.firstName || user?.email}</div>
              <div className="text-[10px] tracking-[0.2em] text-[#F26B1F] uppercase mt-0.5">{user?.platformRole === 'SUPERADMIN' ? 'Superadmin' : 'Platform Admin'}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1E4E8C] to-[#F26B1F] flex items-center justify-center text-white text-sm font-bold shadow-sm">
              {(user?.firstName || user?.email || '?')[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <div className="flex-1 px-8 py-8 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
}
